import type { SupabaseClient } from '@supabase/supabase-js';
import type { Property } from '../types/types';
import { checkShareCompliance } from './share-compliance';

/**
 * Shape needed to validate a property for activation. Matches what the
 * properties list query returns (CRMContext.tsx fetchProperties: `select('*, ...joins')`).
 * Pulled into its own type so the helper can be called from list rows and from
 * the form's working copy without coupling to either.
 */
type PropertyLike = Pick<
  Property,
  | 'title'
  | 'description'
  | 'operation'
  | 'property_type_id'
  | 'price'
  | 'property_status'
  | 'department_id'
  | 'city_id'
  | 'agent_id'
  | 'cover_photo_url'
  | 'gallery'
  | 'image'
>;

export interface ActivationValidation {
  isValid: boolean;
  missing: string[];
}

/**
 * Mirrors the AddPropertyView form's required-field rules so the quick
 * "Activar" button on the card surface can't ship a draft that the form would
 * have blocked. Kept in lockstep manually — single source of truth would be
 * nicer but the form keys against `formData.location.state` / `formData.gallery`
 * while the list row keys against `department_id` / `cover_photo_url`, so a
 * direct share would still need an adapter.
 *
 * The `description` rule is intentionally kept even though it feels soft for a
 * quick activate — the form enforces it on save, so the card has to enforce it
 * too or an agent would be able to bypass via the shortcut.
 */
export function validatePropertyForActivation(p: PropertyLike): ActivationValidation {
  const missing: string[] = [];

  if (!p.title?.trim()) missing.push('Título de la Propiedad');
  if (!p.description?.trim()) missing.push('Descripción');
  if (!p.operation) missing.push('Operación');
  if (!p.property_type_id) missing.push('Tipo de Inmueble');
  if (!p.price || p.price <= 0) missing.push('Precio (USD)');
  if (!p.property_status) missing.push('Estado en Plataforma');
  if (!p.department_id) missing.push('Departamento');
  // "Location tables lie" (see CLAUDE.md): the form's REQUIRED "Sector" field is
  // L3 = properties.city_id; properties.sector_id is the OPTIONAL "Sub-Sector".
  // Validating sector_id here wrongly blocked drafts that have a Sector but no
  // Sub-Sector — the form let them activate, the card's quick-activate didn't.
  if (!p.city_id) missing.push('Sector');
  if (!p.agent_id) missing.push('Agente Responsable');

  const hasPhoto = !!(p.cover_photo_url || p.image || (p.gallery && p.gallery.length > 0));
  if (!hasPhoto) missing.push('Al menos 1 fotografía');

  return { isValid: missing.length === 0, missing };
}

export interface ActivateResult {
  ok: boolean;
  reason?: string;
  /** Set when activation succeeded but the share was dropped for violating
   * Intercambio rules (phone in description / watermark on cover). */
  shareBlocked?: string;
}

/**
 * Promotes a draft property to crm_status='active' and optionally publishes
 * and/or shares it in one save. Mirrors the AddPropertyView "Activar
 * Propiedad" toggle (handleSave) so the two paths produce the same DB state.
 *
 * - When `publish=true`, calls check_publish_limit first (same gate the
 *   kanban toggle and the form use) and aborts on a cap hit.
 * - The DB triggers ([20260409164720_auto_publish_expiry_trigger] + the
 *   share-expiry sibling) populate published_at / publish_expires_at /
 *   shared_at on the UPDATE — we don't set them client-side.
 */
export async function activateProperty(
  supabase: SupabaseClient,
  propertyId: string,
  opts: { publish: boolean; share: boolean; profileId?: string },
): Promise<ActivateResult> {
  if (opts.publish) {
    const { data: limitCheck } = await supabase.rpc('check_publish_limit');
    if (limitCheck && !limitCheck.allowed) {
      return { ok: false, reason: limitCheck.reason || 'Límite de publicaciones alcanzado' };
    }
  }

  // Intercambio rules gate: activation proceeds either way, but a violating
  // property is activated WITHOUT the share and the caller shows the reason.
  let shareBlocked: string | undefined;
  if (opts.share) {
    const compliance = await checkShareCompliance(supabase, propertyId);
    if (!compliance.allowed) shareBlocked = compliance.reasons.join('\n');
  }

  const patch: Record<string, unknown> = {
    crm_status: 'active',
    publish_status: opts.publish ? 'published' : 'unpublished',
    share_status: opts.share && !shareBlocked ? 'shared' : 'not_shared',
  };
  if (opts.profileId) patch.modified_by = opts.profileId;

  const { error } = await supabase.from('properties').update(patch).eq('id', propertyId);
  if (error) return { ok: false, reason: error.message };
  return { ok: true, shareBlocked };
}
